import { afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, output, signal, viewChild } from '@angular/core';
import { GROUPS_WHATS_NEW_STEPS, GroupsWhatsNewStep } from './groups-whats-new.steps';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

@Component({
  selector: 'cmg-groups-whats-new',
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './groups-whats-new.html',
  styleUrl: './groups-whats-new.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'dismiss()',
  },
})
export class GroupsWhatsNew {
  close = output<void>();

  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  readonly steps: readonly GroupsWhatsNewStep[] = GROUPS_WHATS_NEW_STEPS;
  readonly current = signal<number>(0);

  readonly step = computed(() => this.steps[this.current()]);
  readonly isFirst = computed(() => this.current() === 0);
  readonly isLast = computed(() => this.current() === this.steps.length - 1);
  readonly stepCounter = computed(() => `Step ${this.current() + 1} of ${this.steps.length}`);
  readonly progressPct = computed(() => ((this.current() + 1) / this.steps.length) * 100);

  constructor() {
    afterNextRender(() => {
      this.panel()?.nativeElement.focus();
    });
  }

  next(): void {
    if (this.isLast()) {
      this.dismiss();
      return;
    }
    this.current.update((index) => Math.min(this.steps.length - 1, index + 1));
  }

  back(): void {
    this.current.update((index) => Math.max(0, index - 1));
  }

  goTo(index: number): void {
    this.current.set(Math.min(this.steps.length - 1, Math.max(0, index)));
  }

  isCurrent(index: number): boolean {
    return this.current() === index;
  }

  stepDotClass(index: number): string {
    const base = 'h-2 rounded-full transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';
    if (this.isCurrent(index)) {
      return `${base} w-6 bg-primary`;
    }
    if (index < this.current()) {
      return `${base} w-2 bg-primary/40`;
    }
    return `${base} w-2 bg-zinc-300 dark:bg-zinc-600`;
  }

  dismiss(): void {
    this.close.emit();
  }
}
